import { useMemo } from 'react'
import {
  Valuation,
  FinalValuation,
  EstimateLogValuation,
  MultiProviderValuation,
  ValuationSource,
} from '@popety-io/types'
import {
  parseLogValuationData,
  parseMultiProviderValuation,
} from '../utils/valuationParsers'
import { calculateFinalValuation } from '../utils/finalValuationCalculator'

interface UseAutoValuationDataReturn {
  valuations: Valuation[]
  finalValuation: FinalValuation
}

// Helper function to detect MultiProviderValuation format
const isMultiProviderValuation = (
  data: EstimateLogValuation | MultiProviderValuation
): data is MultiProviderValuation => {
  // MultiProviderValuation has providers as top-level keys with price, valueRange, confidence properties
  const keys = Object.keys(data)
  return keys.some(
    (key) =>
      (data as Record<string, any>)[key] &&
      typeof (data as Record<string, any>)[key] === 'object' &&
      'price' in (data as Record<string, any>)[key] &&
      'valueRange' in (data as Record<string, any>)[key] &&
      'confidence' in (data as Record<string, any>)[key]
  )
}

/**
 * Hook to parse and filter automatic (AVM) valuations only
 * Filters out manual valuations created by users
 */
export const useAutoValuationData = (
  logValuation: EstimateLogValuation | MultiProviderValuation | null
): UseAutoValuationDataReturn => {
  // Parse all valuations first
  const allValuations = useMemo(() => {
    if (!logValuation) {
      return []
    }

    // Check if it's MultiProviderValuation format (from AVM API)
    if (isMultiProviderValuation(logValuation)) {
      return parseMultiProviderValuation(logValuation)
    }

    // Otherwise, treat as EstimateLogValuation format (raw logs)
    return parseLogValuationData(logValuation as EstimateLogValuation)
  }, [logValuation])

  // Filter to only automatic valuations (exclude MANUAL source)
  const autoValuations = useMemo(() => {
    return allValuations.filter(
      (valuation) => valuation.source !== ValuationSource.MANUAL
    )
  }, [allValuations])

  // Calculate final valuation from auto valuations only
  const finalValuation = useMemo(() => {
    // Only pass logValuation if it's an EstimateLogValuation, otherwise pass null
    const logValuationForFinal =
      !logValuation || isMultiProviderValuation(logValuation)
        ? null
        : (logValuation as EstimateLogValuation)

    // Calculate final valuation using only auto valuations
    return calculateFinalValuation(logValuationForFinal, autoValuations)
  }, [logValuation, autoValuations])

  return {
    valuations: autoValuations,
    finalValuation,
  }
}
